import { useEffect, useRef, useState } from 'react';
import {
  BenefitCode,
  DeliveryQuote,
  NewFufillmentOrderItem,
  OrderView,
} from '../../__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { graphql } from '../../__generated__';
import { useDoctorCustomerLookup } from './useDoctorCustomerLookup';
import currency from 'currency.js';
import { doctorCustomerFufillmentsQuery } from '../../DoctorCustomerFulfillments/hooks/useDoctorCustomerFulfillments';
import { useDoctorCustomerPromoCodes } from '../../DoctorCustomerPromoCodes/hooks/useDoctorCustomerPromoCodes';
import {
  calculateProductDiscountFromCodes,
  orderViewToDiscountItem,
} from '../../DoctorCustomerPromoCodes/utils/benefitCodesUtils';
import { showErrorToast, showSuccessToast } from '../../Common/utils/toast';
import { PaymentComponentType } from '../../PaymentComponent/types/types';
import { SUBSCRIPTION_SHIPPING_TYPE } from '../../PatientOrder/utils/shippingMethods';

type NewOrder = {
  street: string;
  state: string;
  postcode: string;
  suburb: string;
  deliveryMethod?: string;
  adjusted: boolean;
  quotes: DeliveryQuote[];
  cardSupplied: boolean;
};

export const useDoctorCustomerMenu = (customerID: string, closeModal: () => void) => {
  const [orderMutation] = useMutation(
    graphql(`
      mutation newPortalFufillmentOrder(
        $items: [NewFufillmentOrderItem]
        $deliveryVia: String
        $cardToken: String
        $state: String
        $suburb: String
        $street: String
        $postcode: String
        $customerId: String
        $prescriptionIds: [String]
        $discountCodes: [String]
        $isPayInPerson: Boolean
        $isSubscribing: Boolean
      ) {
        newFufillmentOrderCustomer(
          items: $items
          deliveryVia: $deliveryVia
          cardToken: $cardToken
          prescriptionIds: $prescriptionIds
          state: $state
          street: $street
          suburb: $suburb
          postcode: $postcode
          customerId: $customerId
          discountCodes: $discountCodes
          isPayInPerson: $isPayInPerson
          isSubscribing: $isSubscribing
        ) {
          placed
          error
          order {
            id
          }
        }
      }
    `),
  );

  const { data: unpaidRequests } = useQuery(
    graphql(`
      query unpaidRequestsForCustomer($customerID: String) {
        unpaidRequestsForCustomer(customerID: $customerID) {
          total
          consults {
            appointment {
              date
            }
          }
        }
      }
    `),
    {
      fetchPolicy: 'network-only',
      variables: {
        customerID,
      },
    },
  );

  const [appliedCodes, setAppliedCodes] = useState<BenefitCode[]>([]);

  useDoctorCustomerPromoCodes(customerID, (loadedCodes: BenefitCode[]) => {
    setAppliedCodes(loadedCodes);
  });

  const [error, setError] = useState<string | undefined>(undefined);

  const [selectedItems, setSelectedItems] = useState<OrderView[]>([]);

  const paymentComponent = useRef<PaymentComponentType>(undefined);

  const [placing, setPlacing] = useState(false);

  const [isSubscribing, setIsSubscribing] = useState(false);

  const { customer } = useDoctorCustomerLookup(customerID);

  const [order, setOrder] = useState<NewOrder>({
    street: '',
    state: '',
    postcode: '',
    suburb: '',
    cardSupplied: false,
    adjusted: false,
    quotes: [],
  });

  useEffect(() => {
    if (customer) {
      setOrder((existingState) => ({
        ...existingState,
        state: customer?.state ?? '',
        suburb: customer?.suburb ?? '',
        postcode: customer?.postcode ?? '',
        street: customer?.street ?? '',
      }));
    }
  }, [customer]);

  const [quoteMutation, { called: quotesRetrieved, loading: quotesLoading }] = useMutation(
    graphql(`
      mutation shippingQuoteNew(
        $customerID: String
        $suburb: String
        $street: String
        $state: String
        $postcode: String
        $items: [String]
        $prescriptionIds: [String]
      ) {
        deliveryQuote(
          items: $items
          suburb: $suburb
          state: $state
          postcode: $postcode
          street: $street
          customerID: $customerID
          prescriptionIds: $prescriptionIds
        ) {
          cost
          type
        }
      }
    `),
  );

  const { data, loading: productsLoading } = useQuery(
    graphql(`
      query productsPortal($id: String) {
        customersMenu(customerId: $id) {
          prescription {
            repeatsAvailable
            dispensedDate
            createdAt
            qty
            instructions
            doctor
            id
          }
          medication {
            id
            name
            image
            retail
          }
          message
          status
        }
      }
    `),
    { variables: { id: customerID } },
  );

  const availableProducts = data?.customersMenu?.filter(
    (a) => a?.status === 'AvailableScript' || a?.status === 'NotAvaiableRepeatInterval',
  );

  const availableAccessories = data?.customersMenu?.filter((a) => a?.status === 'Available');

  const itemSelected = (product: OrderView) =>
    selectedItems?.findIndex((a) => a.medication?.id === product?.medication?.id) > -1;

  const toggleItem = (product: OrderView) => {
    setSelectedItems((existingState) => {
      const index = existingState?.findIndex((a) => a.medication?.id === product?.medication?.id);

      if (index > -1) {
        return existingState.filter((a) => a?.medication?.id !== product?.medication?.id);
      } else {
        return [...existingState, product];
      }
    });
  };

  const setValue = (key: keyof NewOrder, value: string | boolean) => {
    if (placing) return;

    setOrder((existingState) => {
      return {
        ...existingState,
        [key]: value,
      };
    });
  };

  const getDeliveryQuotes = async () => {
    const items = selectedItems?.map((a) => a?.medication?.id ?? '');

    const prescriptionIds = [
      ...new Set(selectedItems?.map((a) => a?.prescription?.id ?? '').filter(Boolean) ?? []),
    ];

    const { data } = await quoteMutation({
      variables: {
        street: order.street,
        state: order.state,
        suburb: order.suburb,
        postcode: order.postcode,
        customerID: customerID,
        items,
        prescriptionIds,
      },
    });

    setOrder((existing) => {
      return { ...existing, quotes: data?.deliveryQuote as DeliveryQuote[] };
    });
  };

  const selectedDeliveryMethod = order?.quotes?.find((a) => a?.type === order?.deliveryMethod);

  const subtotal = currency(
    selectedItems?.reduce(
      (currentValue, a) => currentValue + (a.prescription?.qty ?? 1) * (a?.medication?.retail ?? 0),
      0,
    ),
  );

  const deliveryCost = currency(selectedDeliveryMethod?.cost ?? 0);

  const unpaidConsults = currency(unpaidRequests?.unpaidRequestsForCustomer?.total ?? 0);

  const discounts = calculateProductDiscountFromCodes(
    appliedCodes,
    selectedItems?.map((a) => orderViewToDiscountItem(a)),
    selectedDeliveryMethod?.type,
    selectedDeliveryMethod?.cost,
    subtotal.value,
  );

  const total = subtotal.add(deliveryCost).add(unpaidConsults).subtract(discounts.total);

  const canOrder = () => {
    return !!(order?.deliveryMethod &&
    selectedItems?.length > 0 &&
    (paymentComponent?.current?.getPaymentMethod?.() === 'inperson' || order.cardSupplied) &&
    order?.street?.length > 0 &&
    order?.postcode?.length > 0 &&
    order?.suburb?.length > 0 &&
    order?.state?.length > 0);
  }

  const placeOrder = async () => {
    if (!canOrder() || placing) return;

    setPlacing(true);
    setError(undefined);

    // @ts-ignore
    const cardToken = await paymentComponent.current.confirmPayment();

    const prescriptionIds = [
      // @ts-ignore
      ...new Set(selectedItems?.map((a) => a?.prescription?.id ?? '') ?? []),
    ];

    const { data } = await orderMutation({
      variables: {
        customerId: customerID,
        cardToken,
        discountCodes: appliedCodes?.map((a) => a?.code as string) ?? [],
        suburb: order?.suburb,
        state: order?.state,
        street: order?.street,
        postcode: order?.postcode,
        deliveryVia: order?.deliveryMethod,
        prescriptionIds,
        items: selectedItems?.map((item) => {
          const orderItem: NewFufillmentOrderItem = {
            QTY: item.prescription?.qty ?? 1,
            ID: item.medication?.id,
            PrescriptionID: item.prescription?.id,
          };

          return orderItem;
        }),
        isPayInPerson: paymentComponent?.current?.getPaymentMethod?.() === 'inperson',
        isSubscribing
      },
      refetchQueries: [
        {
          query: doctorCustomerFufillmentsQuery,
          variables: { customerID: customerID },
          fetchPolicy: 'network-only',
        },
      ],
    });

    if (!data?.newFufillmentOrderCustomer?.placed) {
      setError(data?.newFufillmentOrderCustomer?.error ?? '');
      setPlacing(false);

      showErrorToast('Error placing order');

      return;
    }

    showSuccessToast('Order placed!');

    closeModal();
  };

  const isSubscribeEligible = () => {
    // repeatsAvailable includes the order being placed right now, so a repeat only
    // remains after it once repeatsAvailable > 1 (e.g. 0 repeats prescribed => 1).
    return !!selectedItems?.find(x => (x.prescription?.repeatsAvailable ?? 0) > 1);
  }

  const onSubscribingChange = (value: boolean) => {
    setIsSubscribing(value);

    setOrder((existingState) => ({
      ...existingState,
      deliveryMethod: value
        ? SUBSCRIPTION_SHIPPING_TYPE
        : existingState.deliveryMethod === SUBSCRIPTION_SHIPPING_TYPE
          ? undefined
          : existingState.deliveryMethod,
    }));
  };

  const toggleDiscountCode = (code: BenefitCode) => {
    if (appliedCodes.findIndex((a) => a?.code === code?.code) > -1) {
      setAppliedCodes((existingState) => {
        return existingState?.filter((a) => a?.code !== code?.code);
      });
    } else {
      setAppliedCodes((existingState) => {
        return [...existingState, code];
      });
    }
  };

  return {
    appliedCodes,
    toggleDiscountCode,
    paymentComponent,
    placeOrder,
    productsLoading,
    error,
    availableAccessories,
    availableProducts,
    unpaidConsults,
    placing,
    getDeliveryQuotes,
    order,
    itemSelected,
    toggleItem,
    quotesRetrieved,
    quotesLoading,
    canOrder,
    isSubscribeEligible,
    isSubscribing,
    onSubscribingChange,
    discountCodes: discounts.usedCodes,
    discounts: discounts.total.format(),
    deliveryCost: deliveryCost.format(),
    subtotal: subtotal.format(),
    total: total,
    setValue,
  };
};
